import * as XLSX from 'xlsx';
import { FILE_EXTENSION_PATTERN } from '../constants/FileUploadConstants';

const buildErrorExcel = (
  originalData: string[][],
  errorMap: Map<number, string[]>
): string => {
  if (!originalData || originalData.length === 0) return '';

  const headers = [...originalData[0], 'Error'];
  const dataRows = originalData.slice(1);

  const worksheetData = [
    headers,
    ...dataRows.map((row, index) => {
      const actualRowNumber = index + 1;
      const errors = errorMap.get(actualRowNumber) || [];
      const errorRow = [...row];
      errorRow[originalData[0].length] = errors.join('; ');
      return errorRow;
    }),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');

  const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([xlsxBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return URL.createObjectURL(blob);
};

const generateErrorFileName = (originalFileName: string): string => {
  const fileNameWithoutExt = originalFileName.replace(
    FILE_EXTENSION_PATTERN,
    ''
  );
  return `${fileNameWithoutExt}_errors.xlsx`;
};

const parseFrontendErrors = (
  rowErrors: Array<{ row: number; errors: string[] }>,
  duplicateGroups: number[][] | undefined,
  t: (key: string, options?: any) => string
): Map<number, string[]> => {
  const errorMap = new Map<number, string[]>();

  rowErrors.forEach(({ row, errors }) => {
    errorMap.set(row, errors);
  });

  duplicateGroups?.forEach((group) => {
    const duplicateErrorMsg = t('duplicate-rows-error-message', {
      rows: group.join(', '),
    });
    group.forEach((rowNum) => {
      const existing = errorMap.get(rowNum) || [];
      errorMap.set(rowNum, [...existing, duplicateErrorMsg]);
    });
  });

  return errorMap;
};

const parseBackendErrors = (backendErrors: string[]): Map<number, string[]> => {
  const errorMap = new Map<number, string[]>();

  backendErrors.forEach((errorStr) => {
    const match = errorStr.match(/Row (\d+):\s*(.+)/i);
    if (match) {
      const rowNumber = parseInt(match[1], 10);
      const errorMessage = match[2];
      const existing = errorMap.get(rowNumber) || [];
      errorMap.set(rowNumber, [...existing, errorMessage]);
    }
  });

  return errorMap;
};

export const generateFrontendErrorCSV = (
  originalData: string[][],
  originalFileName: string,
  rowErrors: Array<{ row: number; errors: string[] }>,
  duplicateGroups: number[][] | undefined,
  t: (key: string, options?: any) => string
): { csvContent: string; fileName: string } => {
  const errorMap = parseFrontendErrors(rowErrors, duplicateGroups, t);
  return {
    csvContent: buildErrorExcel(originalData, errorMap),
    fileName: generateErrorFileName(originalFileName),
  };
};

export const generateBackendErrorCSV = (
  originalData: string[][],
  originalFileName: string,
  backendErrors: string[]
): { csvContent: string; fileName: string } => {
  const errorMap = parseBackendErrors(backendErrors);
  return {
    csvContent: buildErrorExcel(originalData, errorMap),
    fileName: generateErrorFileName(originalFileName),
  };
};
